/**
 * 目录编辑页面
 */
import React, { useState, useEffect, useRef } from 'react';
import { OutlineData, OutlineItem, OutlineMode, OutlineSource, OUTLINE_SOURCE_LABELS, GenerationMode, AnalysisResultMap } from '../types';
import { ExpandFileInfo, expandApi, getErrorMessage, outlineApi, projectApi, readSseStream, uploadOutlineApi } from '../services/api';
import { DocumentTextIcon, TrashIcon, PlusIcon, ArrowDownTrayIcon, ArrowUpTrayIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import OutlineTreeNode from '../components/OutlineTreeNode';
import OutlineProgressLog from '../components/OutlineProgressLog';
import OutlineWordCountSlider from '../components/OutlineWordCountSlider';
import ConfirmDialog from '../components/ConfirmDialog';
import { WORDS_PER_PAGE } from '../config';
import { useAuth } from '../hooks/useAuth';
import { hasFeature } from '../utils/license';

interface OutlineEditProps {
  projectOverview: string;
  techRequirements: string;
  outlineData: OutlineData | null;
  bizOutlineData: OutlineData | null;
  outlineSource: OutlineSource | null;
  generationMode: GenerationMode;
  onOutlineGenerated: (outline: OutlineData, source?: OutlineSource) => void;
  onBizOutlineGenerated: (bizOutline: OutlineData | null) => void;
  onGenerationModeChange: (mode: GenerationMode) => void;
  analysisResults?: AnalysisResultMap;
  projectId?: number;
  projectName?: string;
  outlineDescriptiveLevel?: string;
  readOnly?: boolean;
}

// ---------- 字数辅助工具函数 ----------

/** 从 "800-1500" 格式解析出字数上限 */
const parseWordCountUpper = (range: string | undefined): number => {
  if (!range) return 1500;
  const parts = range.split('-');
  const upper = parseInt(parts[parts.length - 1]);
  return isNaN(upper) ? 1500 : upper;
};

/** 将数字转换为范围字符串 */
const formatWordCountRange = (count: number): string => {
  const lower = Math.round(count * 0.6);
  const upper = Math.round(count);
  return `${lower}-${upper}`;
};

/** 收集所有叶子节点 */
const collectLeafItems = (items: OutlineItem[]): OutlineItem[] => {
  if (!items || !Array.isArray(items)) return [];
  let leaves: OutlineItem[] = [];
  items.forEach(item => {
    if (!item.children || item.children.length === 0) {
      leaves.push(item);
    } else {
      leaves = leaves.concat(collectLeafItems(item.children));
    }
  });
  return leaves;
};

/** 计算所有叶子节点总字数（取上限） */
const calcTotalWords = (items: OutlineItem[]): number => {
  return collectLeafItems(items).reduce((sum, leaf) => {
    return sum + parseWordCountUpper(leaf.word_count_range);
  }, 0);
};

/** 字数转A4页数（从环境变量 REACT_APP_WORDS_PER_PAGE 读取，默认800字/页） */
const wordsToPages = (words: number): number => {
  return Math.ceil(words / WORDS_PER_PAGE);
};

/** 去掉标题中的编号前缀，如 "17 ", "17.1 ", "3.2.1 ", "一、" 等 */
const stripNumPrefix = (title: string): string => {
  return title.replace(/^[\s]*(?:[\d]+(?:\.[\d]+)*[.\)、）\-—]?\s*|[第]?[一二三四五六七八九十百千]+(?:部分|章节|章|节|篇|、|\.|．)[\s]*)+/g, '').trim();
};

/**
 * 判断子节点标题是否与父节点标题冗余。
 * 去掉编号前缀后，若子标题与父标题完全相同或相互包含，视为冗余。
 * e.g. 父="17 技术偏离表" 子="17.1 技术偏离表" → 冗余（编号前缀不同但内容相同）
 * e.g. 父="开标一览表" 子="开标一览表（格式）" → 冗余（父标题包含在子标题中）
 */
const isRedundantTitle = (parentTitle: string, childTitle: string): boolean => {
  const p = stripNumPrefix(parentTitle).toLowerCase();
  const c = stripNumPrefix(childTitle).toLowerCase();
  if (!p || !c) return false;
  if (p === c) return true;
  if (p.length >= 3 && c.length >= 3 && (c.includes(p) || p.includes(c))) return true;
  return false;
};

/**
 * 简化目录树：
 * 1. 移除标题与父节点相同的冗余子节点
 * 2. 合并只有一个子节点的冗余中间层级
 *
 * e.g. "3 开标一览表" → "3.1 开标一览表"（冗余子节点被移除，父节点变为叶子）
 * e.g. "1.1 投标函 → 1.1.1 投标函正文" → 直接保留 "1.1 投标函"
 */
const simplifyOutlineTree = (items: OutlineItem[]): OutlineItem[] => {
  return items.map((item) => {
    const children = item.children || [];
    // 先递归处理子节点
    const simplified = simplifyOutlineTree(children);
    let result = { ...item, title: stripNumPrefix(item.title || ''), children: simplified };

    // 步骤1: 移除标题冗余的子节点
    const parentTitle = item.title || '';
    if (parentTitle && simplified.length > 0) {
      const filtered = simplified.filter((child) => {
        const childTitle = child.title || '';
        if (isRedundantTitle(parentTitle, childTitle)) {
          // 继承被移除子节点的 word_count_range
          if (!item.word_count_range && child.word_count_range) {
            item = { ...item, word_count_range: child.word_count_range };
          }
          return false;
        }
        return true;
      });
      result = { ...item, children: filtered };
    }

    // 步骤2: 合并单子节点
    const currentChildren = result.children || [];
    if (currentChildren.length === 1) {
      const onlyChild = currentChildren[0];
      const wordCountRange = result.word_count_range || onlyChild.word_count_range || undefined;
      result = {
        ...result,
        word_count_range: wordCountRange,
        children: onlyChild.children || [],
      };
    }
    return result;
  });
};

/** 变更项描述 */
interface OutlineRepairChange {
  type: 'merge' | 'remove';
  parentTitle: string;
  childTitle?: string;
}

/**
 * 预览简化操作将产生的变更，返回变更描述列表
 */
const getOutlineRepairChanges = (items: OutlineItem[]): OutlineRepairChange[] => {
  const changes: OutlineRepairChange[] = [];

  const walk = (list: OutlineItem[]) => {
    list.forEach((item) => {
      const children = item.children || [];
      const pTitle = item.title || '';

      // 检查单一子节点（将被合并）
      if (children.length === 1) {
        changes.push({
          type: 'merge',
          parentTitle: pTitle,
          childTitle: children[0].title || '',
        });
      }

      // 检查标题冗余的子节点（将被移除）
      if (children.length > 0) {
        children.forEach((child) => {
          if (isRedundantTitle(pTitle, child.title || '')) {
            changes.push({
              type: 'remove',
              parentTitle: pTitle,
              childTitle: child.title || '',
            });
          }
        });
      }

      // 递归子节点
      walk(children);
    });
  };

  walk(items);
  return changes;
};


/** 紧凑型目录生成模式选择器 */
const TechniqueModeSelector: React.FC<{
  mode: OutlineMode;
  onChange: (mode: OutlineMode) => void;
  disabled?: boolean;
  alignedLabel?: string;
  alignedTitle?: string;
}> = ({ mode, onChange, disabled = false, alignedLabel = '对齐模式', alignedTitle = '一级目录严格按评分大类生成，标题和顺序固定' }) => (
  <div className="flex items-center gap-1 rounded-md border border-gray-300 p-0.5 bg-gray-50">
    <button
      type="button"
      onClick={() => onChange('free')}
      disabled={disabled}
      title="AI 根据项目概述自由组织一级目录"
      className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
        mode === 'free'
          ? 'bg-blue-500 text-white shadow-sm'
          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
      } disabled:cursor-not-allowed disabled:opacity-50`}
    >
      自由模式
    </button>
    <button
      type="button"
      onClick={() => onChange('aligned')}
      disabled={disabled}
      title={alignedTitle}
      className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
        mode === 'aligned'
          ? 'bg-blue-500 text-white shadow-sm'
          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
      } disabled:cursor-not-allowed disabled:opacity-50`}
    >
      {alignedLabel}
    </button>
  </div>
);

const OutlineEdit: React.FC<OutlineEditProps> = ({
  projectOverview,
  techRequirements,
  outlineData,
  bizOutlineData,
  outlineSource,
  generationMode,
  onOutlineGenerated,
  onBizOutlineGenerated,
  onGenerationModeChange,
  analysisResults = {},
  projectId,
  projectName,
  outlineDescriptiveLevel = 'detailed',
  readOnly = false,
}) => {
  const { license } = useAuth();
  const [generating, setGenerating] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [progressLogs, setProgressLogs] = useState<string[]>([]);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editWordCountRange, setEditWordCountRange] = useState('800-1500');
  const [descMaximized, setDescMaximized] = useState(false);
  const [expandFiles, setExpandFiles] = useState<ExpandFileInfo[]>([]);
  const [uploadedExpand, setUploadedExpand] = useState(false);
  const [oldOutline, setOldOutline] = useState<string | null>(null);
  const [oldDocument, setOldDocument] = useState<string | null>(null);
  const [techOutlineMode, setTechOutlineMode] = useState<OutlineMode>('aligned');
  const [bizOutlineMode, setBizOutlineMode] = useState<OutlineMode>('aligned');
  // 全局字数调节滑块
  const [globalWordCount, setGlobalWordCount] = useState<number>(0);
  const [globalWordCountDirty, setGlobalWordCountDirty] = useState(false);
  // 上传目录结构
  const [uploadingOutline, setUploadingOutline] = useState(false);
  const [showUploadOutlineMenu, setShowUploadOutlineMenu] = useState(false);
  const importFileInputRef = useRef<HTMLInputElement>(null);

  // 同步技术目录生成模式：根据已有 outlineSource 恢复之前的模式选择
  useEffect(() => {
    if (outlineSource === 'ai_free') {
      setTechOutlineMode('free');
    } else if (outlineSource === 'ai_aligned') {
      setTechOutlineMode('aligned');
    }
  }, [outlineSource]);

  // 归一化目录项的 ID：强制按树层级重新生成点号编号（如 "1", "1.1", "2.1.3"）
  // stable_id：保留已有的，没有的自动生成（确保 AI 生成的目录也有不可变标识）
  // 同时剥离 content 字段：目录节点不应携带正文，正文由 contentById 独立管理
  const normalizeOutlineIds = (items: OutlineItem[], parentPrefix: string = ''): OutlineItem[] => {
    return items.map((item, index) => {
      const id = parentPrefix
        ? `${parentPrefix}.${index + 1}`
        : (item.id || `${index + 1}`);
      const { content, ...rest } = item;
      return {
        ...rest,
        id,
        title: stripNumPrefix(item.title || ''),
        stable_id: item.stable_id || crypto.randomUUID(),
        children: item.children ? normalizeOutlineIds(item.children, id) : undefined,
      };
    });
  };
  const expandFileInputRef = useRef<HTMLInputElement>(null);

  // 显示修复确认对话框
  const handleRepairOutlineClick = () => {
    if (!outlineData || !outlineData.outline || outlineData.outline.length === 0) {
      setMessage({ type: 'error', text: '没有可修复的目录数据' });
      return;
    }
    setShowRepairOutlineConfirm(true);
  };

  // 执行技术部分目录结构修复
  const handleRepairOutline = () => {
    if (!outlineData || !outlineData.outline) return;
    const simplified = simplifyOutlineTree(outlineData.outline);
    const newOutlineData = { ...outlineData, outline: simplified };
    onOutlineGenerated(newOutlineData);
    setMessage({ type: 'success', text: '目录结构已简化。' });
  };

  // 显示商务部分修复确认对话框
  const handleRepairBizOutlineClick = () => {
    if (!bizOutlineData || !bizOutlineData.outline || bizOutlineData.outline.length === 0) {
      setMessage({ type: 'error', text: '没有可修复的商务部分目录数据' });
      return;
    }
    setShowRepairBizOutlineConfirm(true);
  };

  // 执行商务部分目录结构修复
  const handleRepairBizOutline = () => {
    if (!bizOutlineData || !bizOutlineData.outline) return;
    const simplified = simplifyOutlineTree(bizOutlineData.outline);
    const newBizOutlineData = { ...bizOutlineData, outline: simplified };
    onBizOutlineGenerated(newBizOutlineData);
    setMessage({ type: 'success', text: '商务部分目录结构已简化。' });
  };

  // 导出目录为 JSON 文件
  const handleExportOutline = () => {
    if (!outlineData || !outlineData.outline || outlineData.outline.length === 0) {
      setMessage({ type: 'error', text: '没有可导出的目录数据' });
      return;
    }
    const exportData = {
      outline: outlineData.outline,
      project_name: projectName || '',
      project_overview: projectOverview || '',
      exported_at: new Date().toISOString(),
    };
    const jsonStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const dateStr = new Date().toISOString().slice(0, 10);
    link.download = `${projectName || 'outline'}_backup_${dateStr}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setMessage({ type: 'success', text: '目录已导出' });
  };

  // 触发导入文件选择
  const handleImportClick = () => {
    importFileInputRef.current?.click();
  };

  // 递归为所有节点强制生成全新的 stable_id，导入时切断与旧项目的数据关联
  // 同时剥离 content 字段，防止从其他项目导出的 JSON 携带旧正文
  const regenerateAllStableIds = (items: OutlineItem[]): OutlineItem[] => {
    return items.map((item) => {
      const { content, ...rest } = item;
      return {
        ...rest,
        stable_id: crypto.randomUUID(),
        children: item.children ? regenerateAllStableIds(item.children) : undefined,
      };
    });
  };

  // 从 JSON 文件导入目录
  const handleImportOutline = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        let outlineArray = data.outline || data;
        if (!Array.isArray(outlineArray)) {
          if (Array.isArray(data)) {
            outlineArray = data;
          } else {
            setMessage({ type: 'error', text: '无效的目录文件格式：未找到 outline 数组' });
            return;
          }
        }
        if (outlineArray.length > 0 && !outlineArray[0].title) {
          setMessage({ type: 'error', text: '无效的目录文件格式：节点缺少 title 字段' });
          return;
        }
        // 导入时强制重生所有 stable_id，切断与导出项目的关联
        const fresh = regenerateAllStableIds(outlineArray as OutlineItem[]);
        const normalized = normalizeOutlineIds(fresh);
        onOutlineGenerated({
          outline: normalized,
          project_name: data.project_name || projectName,
          project_overview: data.project_overview || projectOverview,
        }, 'import_json');
        if (projectId) {
          projectApi.saveOutline(projectId, flattenOutlineItems(normalized), 'import_json').catch(() => {});
        }
        setMessage({ type: 'success', text: `目录已导入，共 ${outlineArray.length} 个根节点` });
      } catch {
        setMessage({ type: 'error', text: 'JSON 解析失败，请检查文件格式' });
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  // 处理方案扩写文件上传（支持多文件，累积追加）
  const handleExpandUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;

    const newFiles = Array.from(fileList);
    const existingKeys = new Set(expandFiles.map((f) => `${f.name}::${f.size}`));
    const uniqueNew = newFiles.filter((f) => !existingKeys.has(`${f.name}::${f.size}`));
    if (uniqueNew.length === 0) {
      setMessage({ type: 'error', text: '所选文件已存在，未新增文件' });
      if (expandFileInputRef.current) expandFileInputRef.current.value = '';
      return;
    }

    try {
      setUploadedExpand(true);
      setMessage(null);
      const response = await expandApi.uploadExpandFiles(uniqueNew, projectId);

      if (response.data.success) {
        setUploadedExpand(false);
        setExpandFiles(response.data.files);
        setOldOutline(response.data.old_outline || null);
        setOldDocument(response.data.file_content || null);
        const names = uniqueNew.map((f) => f.name).join('、');
        setMessage({
          type: 'success',
          text: `方案扩写文件新增 ${uniqueNew.length} 个（共 ${response.data.files.length} 个）：${names}`,
        });
      } else {
        throw new Error(response.data.message || '文件上传失败');
      }
    } catch (error) {
      setUploadedExpand(false);
      setMessage({ type: 'error', text: getErrorMessage(error, '文件上传失败') });
    } finally {
      if (expandFileInputRef.current) expandFileInputRef.current.value = '';
    }
  };

  // 移除单个方案扩写文件
  const handleRemoveExpandFile = async (index: number) => {
    const file = expandFiles[index];
    if (!file) return;

    try {
      setUploadedExpand(true);
      const response = await expandApi.deleteExpandFile(file.id, projectId);

      if (response.data.success) {
        setUploadedExpand(false);
        setExpandFiles(response.data.files);
        setOldOutline(response.data.old_outline || null);
        setOldDocument(response.data.file_content || null);
        setMessage({ type: 'success', text: response.data.message });
      } else {
        throw new Error(response.data.message || '删除失败');
      }
    } catch (error) {
      setUploadedExpand(false);
      setMessage({ type: 'error', text: getErrorMessage(error, '删除文件失败') });
    }
  };

  // 上传目录结构文件
  const handleUploadOutlineFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!hasFeature(license?.features, 'ai_generate')) {
      setMessage({ type: 'error', text: '未授权「AI 生成」功能，请联系管理员' });
      return;
    }
    const file = event.target.files?.[0];
    if (!file) return;

    setShowUploadOutlineMenu(false);
    try {
      setUploadingOutline(true);
      setMessage(null);

      const response = await uploadOutlineApi.uploadOutlineFile(file);

      if (response.data.success && response.data.old_outline) {
        const parsedOutline = JSON.parse(response.data.old_outline);
        const outlineItems = Array.isArray(parsedOutline)
          ? parsedOutline
          : (parsedOutline.outline || []);
        // 上传的目录文件可能来自其他项目，强制重生所有 stable_id
        const fresh = regenerateAllStableIds(outlineItems as OutlineItem[]);
        const newOutlineData: OutlineData = {
          outline: normalizeOutlineIds(fresh),
          project_overview: projectOverview,
          project_name: projectName,
        };
        onOutlineGenerated(newOutlineData, 'upload_file');
        if (projectId) {
          projectApi.saveOutline(projectId, flattenOutlineItems(newOutlineData.outline), 'upload_file').catch(() => {});
        }
        setMessage({ type: 'success', text: `目录结构文件上传成功：${file.name}` });
      } else {
        throw new Error(response.data.message || '文件解析失败');
      }
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '目录结构文件上传失败') });
    } finally {
      setUploadingOutline(false);
      try { event.target.value = ''; } catch { /* ignore */ }
    }
  };

  // 上传描述文件生成目录
  const handleUploadDescriptionFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!hasFeature(license?.features, 'ai_generate')) {
      setMessage({ type: 'error', text: '未授权「AI 生成」功能，请联系管理员' });
      return;
    }
    const file = event.target.files?.[0];
    if (!file) return;

    setShowUploadOutlineMenu(false);
    try {
      setUploadingOutline(true);
      setMessage(null);

      const response = await uploadOutlineApi.generateFromDescriptionFile(file);

      if (response.data.success && response.data.old_outline) {
        const parsedOutline = JSON.parse(response.data.old_outline);
        const outlineItems = Array.isArray(parsedOutline)
          ? parsedOutline
          : (parsedOutline.outline || []);
        const descriptionContent = response.data.file_content || '';
        const effectiveOverview = projectOverview.trim()
          ? projectOverview
          : (descriptionContent.slice(0, 2000) || projectOverview);
        // 从文件生成的目录也应使用全新 stable_id，避免潜在跨项目关联
        const fresh = regenerateAllStableIds(outlineItems as OutlineItem[]);
        const newOutlineData: OutlineData = {
          outline: normalizeOutlineIds(fresh),
          project_overview: effectiveOverview,
          project_name: projectName,
        };
        onOutlineGenerated(newOutlineData, 'upload_description_file');
        if (projectId) {
          projectApi.saveOutline(projectId, flattenOutlineItems(newOutlineData.outline), 'upload_description_file').catch(() => {});
        }
        setMessage({ type: 'success', text: `从描述文件生成目录成功：${file.name}` });
      } else {
        throw new Error(response.data.message || '生成目录失败');
      }
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '从描述文件生成目录失败') });
    } finally {
      setUploadingOutline(false);
      try { event.target.value = ''; } catch { /* ignore */ }
    }
  };

  // 根据文档结构描述生成目录
  const handleGenerateFromDescription = async () => {
    if (!hasFeature(license?.features, 'ai_generate')) {
      setMessage({ type: 'error', text: '未授权「AI 生成」功能，请联系管理员' });
      return;
    }
    if (!projectOverview && !techRequirements) {
      setMessage({ type: 'error', text: '请先完成文档分析，获取结构描述' });
      return;
    }

    setShowUploadOutlineMenu(false);
    try {
      setUploadingOutline(true);
      setMessage(null);

      const techScoringDesc = analysisResults['tech_scoring'] || '';
      const combinedTechReq = techScoringDesc
        ? `技术评分标准：\n${techScoringDesc}\n\n技术要求：\n${techRequirements}`
        : techRequirements;

      const response = await uploadOutlineApi.generateFromDescription(
        projectOverview,
        combinedTechReq
      );

      if (response.data.success && response.data.old_outline) {
        const parsedOutline = JSON.parse(response.data.old_outline);
        const outlineItems = Array.isArray(parsedOutline)
          ? parsedOutline
          : (parsedOutline.outline || []);
        const newOutlineData: OutlineData = {
          outline: normalizeOutlineIds(outlineItems as OutlineItem[]),
          project_overview: projectOverview,
          project_name: projectName,
        };
        onOutlineGenerated(newOutlineData, 'from_description');
        if (projectId) {
          projectApi.saveOutline(projectId, flattenOutlineItems(newOutlineData.outline), 'from_description').catch(() => {});
        }
        setMessage({ type: 'success', text: '从文档结构描述生成目录成功' });
      } else {
        throw new Error(response.data.message || '生成目录失败');
      }
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '从文档结构描述生成目录失败') });
    } finally {
      setUploadingOutline(false);
    }
  };

  const [generatingBiz, setGeneratingBiz] = useState(false);
  const [bizProgressLogs, setBizProgressLogs] = useState<string[]>([]);
  const [showBizRegenerateConfirm, setShowBizRegenerateConfirm] = useState(false);
  const [showRepairOutlineConfirm, setShowRepairOutlineConfirm] = useState(false);
  const [showRepairBizOutlineConfirm, setShowRepairBizOutlineConfirm] = useState(false);

  const handleGenerateBizOutline = async () => {
    if (!hasFeature(license?.features, 'ai_generate')) {
      setMessage({ type: 'error', text: '未授权「AI 生成」功能，请联系管理员' });
      return;
    }
    setGeneratingBiz(true);
    setMessage(null);
    setBizProgressLogs([]);

    try {
      const response = await outlineApi.generateBizOutlineStream({
        overview: projectOverview,
        biz_scoring: analysisResults['biz_scoring'] || '',
        bid_requirements: analysisResults['bid_requirements'] || '',
        disqualification: analysisResults['disqualification'] || '',
        commitment: analysisResults['commitment'] || '',
        bid_document_format: analysisResults['bid_document_format'] || '',
        tech_requirements: analysisResults['tech_requirements'] || '',
        mode: bizOutlineMode,
        descriptive_level: outlineDescriptiveLevel,
      });

      let outlineResult: OutlineData | null = null;
      await readSseStream(
        response,
        (event) => {
          if (event.error) {
            throw new Error(event.message || '商务部分目录生成失败');
          }
          if (event.type === 'progress' && event.message) {
            setBizProgressLogs(prev => [...prev, event.message || '']);
            return;
          }
          if (event.type === 'result' && event.outline) {
            outlineResult = event.outline;
          }
        },
        '商务部分目录生成失败'
      );

      if (!outlineResult) {
        throw new Error('未收到商务部分目录生成结果');
      }

      const bizOutline: OutlineData = {
        outline: normalizeOutlineIds((outlineResult as OutlineData).outline ?? (outlineResult as unknown as OutlineItem[])),
        project_overview: projectOverview,
        project_name: projectName,
      };
      onBizOutlineGenerated(bizOutline);

      if (projectId) {
        const bizNodes = flattenOutlineItems(bizOutline.outline, null, 'biz');
        projectApi.saveOutline(projectId, bizNodes, 'ai_free').catch(() => {});
      }

      setMessage({ type: 'success', text: '商务部分目录结构生成完成' });

      // 默认展开所有项目
      const allIds = new Set<string>();
      const collectIds = (items: OutlineItem[]) => {
        items.forEach(item => {
          allIds.add(`biz_${item.id}`);
          if (item.children) collectIds(item.children);
        });
      };
      collectIds(bizOutline.outline);
      setExpandedItems(prev => {
        const next = new Set(prev);
        allIds.forEach(id => next.add(id));
        return next;
      });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '商务部分目录生成失败') });
    } finally {
      setGeneratingBiz(false);
    }
  };

  const handleGenerateOutline = async () => {
    if (!hasFeature(license?.features, 'ai_generate')) {
      setMessage({ type: 'error', text: '未授权「AI 生成」功能，请联系管理员' });
      return;
    }
    if (!projectOverview || !techRequirements) {
      setMessage({ type: 'error', text: '请先完成文档分析' });
      return;
    }

    try {
      setGenerating(true);
      setMessage(null);
      setProgressLogs([]);

      const response = await outlineApi.generateOutlineStream({
        overview: projectOverview,
        requirements: analysisResults['tech_scoring'] || '',
        project_id: projectId,
        project_overview: projectOverview,
        tech_scoring: analysisResults['tech_scoring'] || '',
        tech_requirements: analysisResults['tech_requirements'] || '',
        biz_scoring: analysisResults['biz_scoring'] || '',
        bid_requirements: analysisResults['bid_requirements'] || '',
        commitment: analysisResults['commitment'] || '',
        bid_document_format: analysisResults['bid_document_format'] || '',
        disqualification: analysisResults['disqualification'] || '',
        mode: techOutlineMode,
        uploaded_expand: expandFiles.length > 0,
        old_outline: oldOutline || undefined,
        old_document: oldDocument || undefined,
        descriptive_level: outlineDescriptiveLevel,
      });

      let outlineResult: OutlineData | null = null;
      await readSseStream(
        response,
        (event) => {
          if (event.error) {
            throw new Error(event.message || '目录生成失败');
          }
          if (event.type === 'progress' && event.message) {
            setProgressLogs(prev => [...prev, event.message || '']);
            return;
          }
          if (event.type === 'result' && event.outline) {
            outlineResult = event.outline;
          }
        },
        '目录生成失败'
      );

      if (!outlineResult) {
        throw new Error('未收到目录生成结果');
      }

      const finalOutline: OutlineData = {
        outline: normalizeOutlineIds((outlineResult as OutlineData).outline ?? (outlineResult as unknown as OutlineItem[])),
        project_overview: projectOverview,
        project_name: projectName,
      };

      const outlineSource: OutlineSource = techOutlineMode === 'free' ? 'ai_free' : 'ai_aligned';
      onOutlineGenerated(finalOutline, outlineSource);

      if (projectId) {
        const flatNodes = flattenOutlineItems(finalOutline.outline);
        projectApi.saveOutline(projectId, flatNodes, outlineSource).catch(() => {});
      }

      setMessage({ type: 'success', text: '目录结构生成完成' });

      // 默认展开所有项目
      const allIds = new Set<string>();
      const collectIds = (items: OutlineItem[]) => {
        items.forEach(item => {
          allIds.add(item.id);
          if (item.children) collectIds(item.children);
        });
      };
      collectIds(finalOutline.outline);
      setExpandedItems(allIds);
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '目录生成失败') });
    } finally {
      setGenerating(false);
    }
  };


  const toggleExpanded = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  // 开始编辑目录项
  const startEditing = (item: OutlineItem) => {
    setEditingItem(item.id);
    setEditTitle(item.title);
    setEditDescription(item.description);
    setEditWordCountRange(item.word_count_range || '800-1500');
    setDescMaximized(false);
  };

  // 取消编辑
  const cancelEditing = () => {
    setEditingItem(null);
    setEditTitle('');
    setEditDescription('');
    setEditWordCountRange('800-1500');
    setDescMaximized(false);
  };

  // 保存编辑
  const saveEdit = () => {
    if (!outlineData || !editingItem) return;

    const updateItem = (items: OutlineItem[]): OutlineItem[] => {
      return items.map(item => {
        if (item.id === editingItem) {
          return {
            ...item,
            title: editTitle.trim(),
            description: editDescription.trim(),
            word_count_range: editWordCountRange.trim() || '800-1500'
          };
        }
        if (item.children) {
          return { ...item, children: updateItem(item.children) };
        }
        return item;
      });
    };

    const updatedOutline = updateItem(outlineData.outline);
    const updatedData = { ...outlineData, outline: updatedOutline };

    onOutlineGenerated(updatedData);
    cancelEditing();
    setMessage({ type: 'success', text: '目录项更新成功' });

    if (projectId) {
      projectApi.saveOutline(projectId, flattenOutlineItems(updatedOutline)).catch(() => {});
    }
  };

  // 重新分配序号的函数
  const reorderItems = (items: OutlineItem[], parentPrefix: string = ''): OutlineItem[] => {
    return items.map((item, index) => {
      const newId = parentPrefix ? `${parentPrefix}.${index + 1}` : `${index + 1}`;
      return {
        ...item,
        id: newId,
        // stable_id：保留已有的，没有的自动生成
        stable_id: item.stable_id || crypto.randomUUID(),
        children: item.children ? reorderItems(item.children, newId) : undefined
      };
    });
  };

  // 删除目录项
  const deleteItem = (itemId: string) => {
    if (!outlineData) return;

    if (window.confirm('确定要删除这个目录项吗？')) {
      const deleteFromItems = (items: OutlineItem[]): OutlineItem[] => {
        return items.flatMap(item => {
          if (item.id === itemId) return [];
          const nextItem = item.children
            ? { ...item, children: deleteFromItems(item.children) }
            : item;
          return [nextItem];
        });
      };

      const filteredItems = deleteFromItems(outlineData.outline);
      const reorderedItems = reorderItems(filteredItems);

      const updatedData = { ...outlineData, outline: reorderedItems };

      onOutlineGenerated(updatedData);
      setMessage({ type: 'success', text: '目录项删除成功' });

      if (projectId) {
        projectApi.saveOutline(projectId, flattenOutlineItems(reorderedItems)).catch(() => {});
      }
    }
  };

  // 添加子目录项
  const addChildItem = (parentId: string) => {
    if (!outlineData) return;

    const findParentAndGetNextId = (items: OutlineItem[], targetParentId: string): string | null => {
      for (const item of items) {
        if (item.id === targetParentId) {
          const existingChildren = item.children || [];
          let maxChildNum = 0;
          existingChildren.forEach(child => {
            const childIdParts = child.id.split('.');
            const lastPart = childIdParts[childIdParts.length - 1];
            const num = parseInt(lastPart);
            if (!isNaN(num)) maxChildNum = Math.max(maxChildNum, num);
          });
          return `${parentId}.${maxChildNum + 1}`;
        }
        if (item.children) {
          const result = findParentAndGetNextId(item.children, targetParentId);
          if (result) return result;
        }
      }
      return null;
    };

    const newId = findParentAndGetNextId(outlineData.outline, parentId) || `${parentId}.1`;
    const newItem: OutlineItem = {
      id: newId,
      stable_id: crypto.randomUUID(),
      title: '新目录项',
      description: '请编辑描述'
    };

    const addToItems = (items: OutlineItem[]): OutlineItem[] => {
      return items.map(item => {
        if (item.id === parentId) {
          return { ...item, children: [...(item.children || []), newItem] };
        }
        if (item.children) {
          return { ...item, children: addToItems(item.children) };
        }
        return item;
      });
    };

    const updatedData = { ...outlineData, outline: addToItems(outlineData.outline) };

    onOutlineGenerated(updatedData);

    setExpandedItems(prev => {
      const newSet = new Set(prev);
      newSet.add(parentId);
      return newSet;
    });

    setTimeout(() => startEditing(newItem), 100);

    setMessage({ type: 'success', text: '子目录添加成功' });

    if (projectId) {
      projectApi.saveOutline(projectId, flattenOutlineItems(updatedData.outline)).catch(() => {});
    }
  };

  // 添加根目录项
  const addRootItem = () => {
    if (!outlineData) return;

    let maxRootNum = 0;
    outlineData.outline.forEach(item => {
      const idParts = item.id.split('.');
      const firstPart = idParts[0];
      const num = parseInt(firstPart);
      if (!isNaN(num)) maxRootNum = Math.max(maxRootNum, num);
    });

    const newId = `${maxRootNum + 1}`;
    const newItem: OutlineItem = {
      id: newId,
      stable_id: crypto.randomUUID(),
      title: '新目录项',
      description: '请编辑描述'
    };

    const updatedOutline = [...outlineData.outline, newItem];
    const updatedData = { ...outlineData, outline: updatedOutline };

    onOutlineGenerated(updatedData);

    setTimeout(() => startEditing(newItem), 100);

    setMessage({ type: 'success', text: '目录项添加成功' });

    if (projectId) {
      projectApi.saveOutline(projectId, flattenOutlineItems(updatedOutline)).catch(() => {});
    }
  };

  // 全局字数调节：均分到每个叶子节点
  const handleGlobalWordCountChange = (newTotal: number) => {
    if (!outlineData) return;
    setGlobalWordCount(newTotal);
    setGlobalWordCountDirty(true);

    const leaves = collectLeafItems(outlineData.outline);
    const leafCount = leaves.length;
    if (leafCount <= 0) return;

    const perLeaf = Math.round(newTotal / leafCount);

    const updateWordCount = (items: OutlineItem[]): OutlineItem[] => {
      return items.map(item => {
        const isLeaf = !item.children || item.children.length === 0;
        if (isLeaf) return { ...item, word_count_range: formatWordCountRange(perLeaf) };
        if (item.children) return { ...item, children: updateWordCount(item.children) };
        return item;
      });
    };

    const updatedOutline = updateWordCount(outlineData.outline);
    const updatedData = { ...outlineData, outline: updatedOutline };
    onOutlineGenerated(updatedData);

    if (projectId) {
      projectApi.saveOutline(projectId, flattenOutlineItems(updatedOutline)).catch(() => {});
    }
  };

  // 当 outlineData 变化时同步全局字数
  useEffect(() => {
    if (outlineData && !globalWordCountDirty) {
      const total = calcTotalWords(outlineData.outline);
      setGlobalWordCount(total);
      setGlobalWordCountDirty(false);
    }
  }, [outlineData, globalWordCountDirty]);

  // 页面加载时恢复已保存的方案扩写文件（按项目隔离）
  useEffect(() => {
    if (!projectId) return;
    expandApi
      .getExpandFiles(projectId)
      .then((res) => {
        if (res.data.success && res.data.files.length > 0) {
          setExpandFiles(res.data.files);
          setOldOutline(res.data.old_outline || null);
          setOldDocument(res.data.file_content || null);
        }
      })
      .catch(() => { /* 静默失败 */ });
  }, [projectId]);

  // 切换项目时清除上一个项目的 expandFiles 状态
  useEffect(() => {
    setExpandFiles([]);
    setOldOutline(null);
    setOldDocument(null);
    setUploadedExpand(false);
  }, [projectId]);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* 只读提示横幅 */}
      {readOnly && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m0-10v4m0 0a2 2 0 100-4 2 2 0 000 4z" />
          </svg>
          <span className="text-sm text-amber-700">此项目为只读共享，目录编辑操作已禁用。</span>
        </div>
      )}

      {/* 操作按钮 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">📋 目录管理</h2>
        
        <div className="space-y-4">
          {/* 生成模式选择 */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">投标文件生成模式</p>
            <div className="grid gap-3 md:grid-cols-2">
              <button
                type="button"
                onClick={() => onGenerationModeChange('tech')}
                disabled={generating || generatingBiz || readOnly}
                className={`rounded-lg border px-4 py-3 text-left transition-colors ${
                  generationMode === 'tech'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                } disabled:cursor-not-allowed disabled:opacity-60`}
              >
                <div className="text-sm font-medium">技术方案</div>
                <p className="mt-1 text-xs leading-5 text-gray-500">
                  仅生成技术标部分，与现有流程一致。
                </p>
              </button>
              <button
                type="button"
                onClick={() => onGenerationModeChange('full')}
                disabled={generating || generatingBiz || readOnly}
                className={`rounded-lg border px-4 py-3 text-left transition-colors ${
                  generationMode === 'full'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                } disabled:cursor-not-allowed disabled:opacity-60`}
              >
                <div className="text-sm font-medium">整套投标文件</div>
                <p className="mt-1 text-xs leading-5 text-gray-500">
                  生成商务部分 + 技术部分完整投标文件。
                </p>
              </button>
            </div>
          </div>



          <div className="flex flex-wrap items-center gap-4">
          {/* 方案扩写按钮 */}
          <div className="relative">
            <input
              ref={expandFileInputRef}
              type="file"
              id="expand-file-upload"
              accept=".pdf,.doc,.docx,.md,.markdown,.txt"
              multiple
              onChange={handleExpandUpload}
              className="hidden"
              disabled={uploadedExpand}
            />
            <label
              htmlFor={readOnly ? undefined : "expand-file-upload"}
              title={readOnly ? '只读模式，无法上传文件' : '上传旧方案（以往投标文件或技术方案）的结构和内容来扩写新目录结构'}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                readOnly
                  ? 'bg-gray-400 cursor-not-allowed'
                  : uploadedExpand
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 cursor-pointer'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500`}
            >
              {uploadedExpand ? (
                <>
                  <div className="animate-spin -ml-1 mr-3 h-4 w-4 text-white">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  正在分析...
                </>
              ) : (
                '方案扩写'
              )}
            </label>
          </div>

          <button
            onClick={handleGenerateOutline}
            disabled={generating || !projectOverview || !techRequirements || readOnly}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
          >
            {generating ? (
              <>
                <div className="animate-spin -ml-1 mr-3 h-4 w-4 text-white">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                正在生成目录...
              </>
            ) : (
              '生成技术目录结构'
            )}
          </button>

          {/* 技术目录生成模式 */}
          <TechniqueModeSelector
            mode={techOutlineMode}
            onChange={setTechOutlineMode}
            disabled={generating || generatingBiz || readOnly}
          />

          {/* 上传技术目录结构按钮（带下拉菜单） */}
          <div className="relative flex items-center gap-2">
            <button
              onClick={() => setShowUploadOutlineMenu(!showUploadOutlineMenu)}
              disabled={uploadingOutline || readOnly}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:bg-gray-400"
            >
              {uploadingOutline ? (
                <>
                  <div className="animate-spin -ml-1 mr-3 h-4 w-4 text-white">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  正在处理...
                </>
              ) : (
                '上传技术目录结构'
              )}
            </button>
            {outlineData?.outline && outlineData.outline.length > 0 && outlineSource && (
              <span className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded">
                {OUTLINE_SOURCE_LABELS[outlineSource]}
              </span>
            )}

            {/* 下拉菜单 */}
            {showUploadOutlineMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowUploadOutlineMenu(false)} />
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-md shadow-lg border border-gray-200 z-20 py-1">
                  <input type="file" id="outline-file-upload" accept=".json,.txt" onChange={handleUploadOutlineFile} className="hidden" />
                  <label htmlFor="outline-file-upload" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <div className="font-medium">上传目录结构文件</div>
                    <div className="text-xs text-gray-500 mt-0.5">上传 JSON/TXT 格式的目录结构</div>
                  </label>

                  <div className="border-t border-gray-100 my-1" />

                  <input type="file" id="desc-file-upload" accept=".pdf,.doc,.docx,.txt,.md,.markdown" onChange={handleUploadDescriptionFile} className="hidden" />
                  <label htmlFor="desc-file-upload" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <div className="font-medium">上传描述文件生成</div>
                    <div className="text-xs text-gray-500 mt-0.5">上传 PDF/Word/TXT/Markdown 描述文件，AI 提取目录</div>
                  </label>

                  <button
                    onClick={handleGenerateFromDescription}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <div className="font-medium">从已有描述生成</div>
                    <div className="text-xs text-gray-500 mt-0.5">基于项目概述和技术评分标准</div>
                  </button>
                </div>
              </>
            )}
          </div>
          </div>

          {/* 商务部分：独立一行 */}
          {generationMode === 'full' && (
            <div className="flex flex-wrap items-center gap-4 mt-3">
              <button
                onClick={handleGenerateBizOutline}
                disabled={generatingBiz || !projectOverview || readOnly}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:bg-gray-400"
              >
                {generatingBiz ? (
                  <>
                    <div className="animate-spin -ml-1 mr-3 h-4 w-4 text-white">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    正在生成商务部分目录...
                  </>
                ) : (
                  '生成商务部分目录'
                )}
              </button>

              {/* 商务目录生成模式 */}
              <TechniqueModeSelector
                mode={bizOutlineMode}
                onChange={setBizOutlineMode}
                disabled={generating || generatingBiz || readOnly}
                alignedLabel="与投标要求及投标文件格式一一对应"
                alignedTitle="一级目录严格按照投标要求和投标文件格式生成，标题和顺序与招标文件一致"
              />
            </div>
          )}
        </div>

        {/* 显示已上传的方案扩写文件（支持逐个删除） */}
        {expandFiles.length > 0 && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-start">
              <DocumentTextIcon className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-sm text-green-800">
                  已上传方案扩写参考文件（{expandFiles.length} 个）：
                </span>
                <ul className="mt-1 space-y-1">
                  {expandFiles.map((f, i) => (
                    <li key={`${f.name}-${i}-${f.size}`} className="flex items-center justify-between text-sm pl-2 pr-1 py-0.5 rounded hover:bg-green-100 group">
                      <span className="text-green-700 font-medium truncate mr-2">{f.name}</span>
                      <button
                        onClick={() => handleRemoveExpandFile(i)}
                        disabled={uploadedExpand || readOnly}
                        className="p-0.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                        title="移除此文件"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {!projectOverview && !techRequirements && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              请先在"标书解析"步骤中完成文档分析，获取项目概述和技术评分标准。
            </p>
          </div>
        )}

        {/* 技术部分生成过程显示 */}
        <OutlineProgressLog
          logs={progressLogs}
          isGenerating={generating}
          label="目录结构"
        />

        {/* 商务部分生成过程显示 */}
        <OutlineProgressLog
          logs={bizProgressLogs}
          isGenerating={generatingBiz}
          label="商务部分目录"
          bgColor="bg-orange-50"
          borderColor="border-orange-200"
          titleColor="text-orange-800"
        />
      </div>

      {/* 技术部分目录结构显示 */}
      {outlineData && Array.isArray(outlineData.outline) && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <h3 className="text-lg font-medium text-gray-900">第二部分 技术部分目录</h3>
              <div className="flex items-center space-x-3 text-sm">
                <span className="text-gray-500">
                  全文约 <span className="font-semibold text-blue-600">{calcTotalWords(outlineData.outline).toLocaleString()}</span> 字
                </span>
                <span className="text-gray-300">|</span>
                <span className="text-gray-500">
                  约 A4 <span className="font-semibold text-green-600">{wordsToPages(calcTotalWords(outlineData.outline))}</span> 页
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="file"
                ref={importFileInputRef}
                onChange={handleImportOutline}
                accept=".json"
                className="hidden"
                disabled={readOnly}
              />
              {!readOnly && (
              <button
                onClick={handleRepairOutlineClick}
                title="合并只有一个子节点的冗余层级，移除与父节点标题重复的子节点"
                className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <WrenchScrewdriverIcon className="h-4 w-4 mr-1" />
                简化层级
              </button>
              )}
              {!readOnly && (
              <button
                onClick={handleImportClick}
                title="从 JSON 文件导入目录结构"
                className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowUpTrayIcon className="h-4 w-4 mr-1" />
                导入目录
              </button>
              )}
              <button
                onClick={handleExportOutline}
                title="导出目录结构为 JSON 文件"
                className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                导出目录
              </button>
              {!readOnly && (
              <button
                onClick={addRootItem}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                添加目录项
              </button>
              )}
            </div>
          </div>

          {/* 全文字数调节滑动条 */}
          {!readOnly && (
          <OutlineWordCountSlider
            globalWordCount={globalWordCount}
            onWordCountChange={handleGlobalWordCountChange}
          />
          )}

          <div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
            {outlineData.outline.map(item => (
              <OutlineTreeNode
                key={item.id}
                item={item}
                level={0}
                expandedItems={expandedItems}
                editingItem={editingItem}
                editTitle={editTitle}
                editDescription={editDescription}
                editWordCountRange={editWordCountRange}
                descMaximized={descMaximized}
                onToggleExpand={toggleExpanded}
                onStartEdit={startEditing}
                onSaveEdit={saveEdit}
                onCancelEdit={cancelEditing}
                onSetEditTitle={setEditTitle}
                onSetEditDescription={setEditDescription}
                onSetEditWordCountRange={setEditWordCountRange}
                onToggleDescMaximized={setDescMaximized}
                onDeleteItem={deleteItem}
                onAddChildItem={addChildItem}
                parseWordCountUpper={parseWordCountUpper}
                readOnly={readOnly}
              />
            ))}
          </div>
        </div>
      )}

      {/* 商务部分目录结构显示 */}
      {generationMode === 'full' && bizOutlineData && Array.isArray(bizOutlineData.outline) && (
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-400">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <h3 className="text-lg font-medium text-gray-900">第一部分 商务部分目录</h3>
              <div className="flex items-center space-x-3 text-sm">
                <span className="text-gray-500">
                  全文约 <span className="font-semibold text-orange-600">{calcTotalWords(bizOutlineData.outline).toLocaleString()}</span> 字
                </span>
                <span className="text-gray-300">|</span>
                <span className="text-gray-500">
                  约 A4 <span className="font-semibold text-green-600">{wordsToPages(calcTotalWords(bizOutlineData.outline))}</span> 页
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {!readOnly && (
              <button
                onClick={handleRepairBizOutlineClick}
                title="合并只有一个子节点的冗余层级，移除与父节点标题重复的子节点"
                className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <WrenchScrewdriverIcon className="h-4 w-4 mr-1" />
                简化层级
              </button>
              )}
              {!readOnly && (
              <button
                onClick={() => onBizOutlineGenerated(null)}
                className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                title="清除商务部分目录"
              >
                清除
              </button>
              )}
              {!readOnly && (
              <button
                onClick={() => setShowBizRegenerateConfirm(true)}
                disabled={generatingBiz}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400"
              >
                {generatingBiz ? '生成中...' : '重新生成'}
              </button>
              )}
            </div>
          </div>

          <div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
            {bizOutlineData.outline.map(item => (
              <OutlineTreeNode
                key={item.id}
                item={item}
                level={0}
                expandedItems={expandedItems}
                editingItem={editingItem}
                editTitle={editTitle}
                editDescription={editDescription}
                editWordCountRange={editWordCountRange}
                descMaximized={descMaximized}
                onToggleExpand={toggleExpanded}
                onStartEdit={startEditing}
                onSaveEdit={saveEdit}
                onCancelEdit={cancelEditing}
                onSetEditTitle={setEditTitle}
                onSetEditDescription={setEditDescription}
                onSetEditWordCountRange={setEditWordCountRange}
                onToggleDescMaximized={setDescMaximized}
                onDeleteItem={deleteItem}
                onAddChildItem={addChildItem}
                parseWordCountUpper={parseWordCountUpper}
                readOnly={readOnly}
              />
            ))}
          </div>
        </div>
      )}

      {/* 消息提示 */}
      {message && (
        <div className={`p-4 rounded-md ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-700 border border-green-200' 
            : 'bg-red-100 text-red-700 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* 技术部分目录简化确认 */}
      {outlineData && (
        <ConfirmDialog
          open={showRepairOutlineConfirm}
          title="确认简化技术部分目录结构？"
          confirmText="确认简化"
          cancelText="取消"
          confirmClassName="bg-blue-600 hover:bg-blue-700"
          onConfirm={() => {
            setShowRepairOutlineConfirm(false);
            handleRepairOutline();
          }}
          onCancel={() => setShowRepairOutlineConfirm(false)}
        >
          {(() => {
            const changes = getOutlineRepairChanges(outlineData.outline);
            if (changes.length === 0) {
              return <p className="text-sm text-gray-600">没有发现可简化的冗余层级，目录结构已经很简洁。</p>;
            }
            return (
              <div className="space-y-2">
                <p className="text-sm text-gray-600 mb-2">将对以下 {changes.length} 处冗余层级进行简化：</p>
                <div className="max-h-60 overflow-y-auto space-y-1.5">
                  {changes.map((c, i) => (
                    <div key={i} className="text-sm text-gray-700 flex items-start gap-2">
                      {c.type === 'merge' ? (
                        <>
                          <span className="text-blue-500 shrink-0 mt-0.5">→</span>
                          <span>合并层级：<span className="font-medium text-gray-900">「{c.parentTitle}」</span>的唯一子节点<span className="font-medium text-gray-900">「{c.childTitle}」</span></span>
                        </>
                      ) : (
                        <>
                          <span className="text-red-500 shrink-0 mt-0.5">−</span>
                          <span>移除冗余：<span className="font-medium text-gray-900">「{c.parentTitle}」</span>下的<span className="font-medium text-gray-900">「{c.childTitle}」</span>（标题与父节点重复）</span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </ConfirmDialog>
      )}

      {/* 商务部分目录简化确认 */}
      {bizOutlineData && (
        <ConfirmDialog
          open={showRepairBizOutlineConfirm}
          title="确认简化商务部分目录结构？"
          confirmText="确认简化"
          cancelText="取消"
          confirmClassName="bg-blue-600 hover:bg-blue-700"
          onConfirm={() => {
            setShowRepairBizOutlineConfirm(false);
            handleRepairBizOutline();
          }}
          onCancel={() => setShowRepairBizOutlineConfirm(false)}
        >
          {(() => {
            const changes = getOutlineRepairChanges(bizOutlineData.outline);
            if (changes.length === 0) {
              return <p className="text-sm text-gray-600">没有发现可简化的冗余层级，目录结构已经很简洁。</p>;
            }
            return (
              <div className="space-y-2">
                <p className="text-sm text-gray-600 mb-2">将对以下 {changes.length} 处冗余层级进行简化：</p>
                <div className="max-h-60 overflow-y-auto space-y-1.5">
                  {changes.map((c, i) => (
                    <div key={i} className="text-sm text-gray-700 flex items-start gap-2">
                      {c.type === 'merge' ? (
                        <>
                          <span className="text-blue-500 shrink-0 mt-0.5">→</span>
                          <span>合并层级：<span className="font-medium text-gray-900">「{c.parentTitle}」</span>的唯一子节点<span className="font-medium text-gray-900">「{c.childTitle}」</span></span>
                        </>
                      ) : (
                        <>
                          <span className="text-red-500 shrink-0 mt-0.5">−</span>
                          <span>移除冗余：<span className="font-medium text-gray-900">「{c.parentTitle}」</span>下的<span className="font-medium text-gray-900">「{c.childTitle}」</span>（标题与父节点重复）</span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </ConfirmDialog>
      )}

      {/* 商务部分目录重新生成确认 */}
      <ConfirmDialog
        open={showBizRegenerateConfirm}
        title="确认重新生成商务部分目录？"
        confirmText="确认"
        cancelText="取消"
        confirmClassName="bg-orange-600 hover:bg-orange-700"
        onConfirm={() => {
          setShowBizRegenerateConfirm(false);
          handleGenerateBizOutline();
        }}
        onCancel={() => setShowBizRegenerateConfirm(false)}
      >
        <p className="text-sm text-gray-600">
          重新生成将覆盖当前商务部分目录结构，包括所有已编辑的节点和内容。
          请确认是否继续？
        </p>
      </ConfirmDialog>
    </div>
  );
};

/**
 * 将树形 OutlineItem[] 平铺为数据库格式
 */
function flattenOutlineItems(
  items: OutlineItem[],
  parentNodeId: string | null = null,
  section: string = 'tech'
): Array<{
  id: string;
  stable_id?: string | null;
  parent_node_id: string | null;
  title: string;
  description: string;
  word_count_range?: string | null;
  source_requirement_id?: string | null;
  source_requirement_title?: string | null;
  section: string;
}> {
  const result: ReturnType<typeof flattenOutlineItems> = [];
  for (const item of items) {
    result.push({
      id: item.id,
      stable_id: item.stable_id || null,
      parent_node_id: parentNodeId,
      title: item.title,
      description: item.description || '',
      word_count_range: item.word_count_range || '800-1500',
      source_requirement_id: item.source_requirement_id || null,
      source_requirement_title: item.source_requirement_title || null,
      section,
    });
    if (item.children && item.children.length > 0) {
      result.push(...flattenOutlineItems(item.children, item.id, section));
    }
  }
  return result;
}

export default OutlineEdit;
